import React, { useState } from 'react';
import {
  MagnifyingGlassIcon,
  GlobeAltIcon,
  ComputerDesktopIcon,
  ClockIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Toaster, toast } from 'react-hot-toast';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import axios from 'axios';
import AdBanner from './AdBanner';
import './App.css';

// Fix Leaflet default marker icon (CRA/webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// ─── Constants ────────────────────────────────────────────────────────────────
const MAX_HISTORY = 20;
const HISTORY_KEY = 'iptracker_history';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (ts) => {
  if (!ts) return null;
  return new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const loadHistory = () => {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
  catch { return []; }
};

// ─── InfoRow ──────────────────────────────────────────────────────────────────
function InfoRow({ label, value, mono = false }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className={mono ? 'font-mono text-[#00ff88] text-sm break-all' : 'text-[#c8d8e8] text-sm'}>
        {value}
      </div>
    </div>
  );
}

// ─── SectionCard ─────────────────────────────────────────────────────────────
function SectionCard({ title, accent, children }) {
  return (
    <div
      className="result-section rounded-xl p-5 bg-[#0a1628]/90 border border-white/5"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <h3 className="text-xs font-mono font-semibold uppercase tracking-widest mb-4" style={{ color: accent }}>
        {'// '}{title}
      </h3>
      {children}
    </div>
  );
}

// ─── MapView ──────────────────────────────────────────────────────────────────
function MapView({ coordinates, label }) {
  if (!coordinates?.latitude || !coordinates?.longitude) return null;
  const lat = parseFloat(coordinates.latitude);
  const lng = parseFloat(coordinates.longitude);
  if (isNaN(lat) || isNaN(lng)) return null;

  return (
    <MapContainer center={[lat, lng]} zoom={10} className="leaflet-map" scrollWheelZoom={false}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
        url={`https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`}
      />
      <Marker position={[lat, lng]}>
        <Popup>{label}</Popup>
      </Marker>
    </MapContainer>
  );
}

// ─── DnsRecordList ────────────────────────────────────────────────────────────
function DnsRecordList({ label, records, getVal = (r) => r }) {
  if (!records?.length) return null;
  return (
    <div>
      <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className="space-y-1">
        {records.map((r, i) => (
          <div key={i} className="font-mono text-sm text-[#00ff88] bg-[#060d18] px-3 py-1.5 rounded border border-white/5">
            {getVal(r)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── HistoryPanel ─────────────────────────────────────────────────────────────
function HistoryPanel({ history, onSelect, onClear }) {
  if (history.length === 0) return null;
  return (
    <div className="max-w-2xl mx-auto mb-8">
      <div className="rounded-xl bg-[#0a1628]/80 border border-[#00d4ff]/10 px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <ClockIcon className="w-4 h-4 text-[#00d4ff]/40" />
            <span className="text-xs uppercase tracking-widest text-[#00d4ff]/40 font-mono">Recent</span>
          </div>
          <button
            onClick={onClear}
            className="flex items-center space-x-1 text-xs text-[#2a4060] hover:text-[#ff4d6d] transition-colors"
          >
            <TrashIcon className="w-3.5 h-3.5" />
            <span>Clear</span>
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
          {history.map((entry) => (
            <button
              key={entry.id}
              onClick={() => onSelect(entry)}
              title={`${entry.type.toUpperCase()} · ${new Date(entry.timestamp).toLocaleString()}`}
              className="flex-shrink-0 flex items-center space-x-2 bg-[#060d18] hover:bg-[#0d1f36]
                         text-[#c8d8e8] text-xs px-3 py-2 rounded-lg transition-all
                         border border-[#00d4ff]/10 hover:border-[#00d4ff]/30"
            >
              {entry.type === 'ip'
                ? <ComputerDesktopIcon className="w-3.5 h-3.5 text-[#00d4ff]" />
                : <GlobeAltIcon className="w-3.5 h-3.5 text-[#00ff88]" />
              }
              <span className="font-mono">{entry.query}</span>
              {entry.preview && (
                <span className="text-[#2a4060]">· {entry.preview}</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── IPResult ─────────────────────────────────────────────────────────────────
function IPResult({ result }) {
  const coords = result.geolocation?.coordinates;
  const mapLabel = [result.geolocation?.city, result.geolocation?.country]
    .filter(Boolean).join(', ') || result.ip;

  return (
    <div className="space-y-4">

      {/* Basic Info */}
      <SectionCard title="Basic Information" accent="#00d4ff">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-1">IP Address</div>
            <div className="font-mono text-[#00d4ff] text-xl tracking-widest">{result.ip}</div>
          </div>
          {result.sources?.length > 0 && (
            <div>
              <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-2">Data Sources</div>
              <div className="flex flex-wrap gap-1">
                {result.sources.map((s) => (
                  <span key={s} className="text-xs font-mono bg-[#060d18] text-[#00d4ff]/60 border border-[#00d4ff]/15 px-2 py-0.5 rounded">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          {result.cached && (
            <span className="inline-flex items-center gap-1.5 text-xs font-mono bg-[#fbbf24]/10 text-[#fbbf24] border border-[#fbbf24]/20 px-2.5 py-1 rounded w-fit">
              ⚡ cached
            </span>
          )}
        </div>
      </SectionCard>

      {/* Geolocation + Map */}
      {result.geolocation && (
        <SectionCard title="Geolocation" accent="#00d4ff">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoRow label="Country"  value={result.geolocation.country} />
            <InfoRow label="Region"   value={result.geolocation.region} />
            <InfoRow label="City"     value={result.geolocation.city} />
            <InfoRow label="ZIP Code" value={result.geolocation.zip} mono />
            <InfoRow label="Timezone" value={result.geolocation.timezone} />
            {coords && (
              <div>
                <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-1">Coordinates</div>
                <div className="font-mono text-[#00ff88] text-sm">
                  {coords.latitude}, {coords.longitude}
                  {coords.accuracyRadius && (
                    <span className="text-[#00d4ff]/30 ml-1 text-xs">(±{coords.accuracyRadius} km)</span>
                  )}
                </div>
              </div>
            )}
          </div>
          <MapView coordinates={coords} label={mapLabel} />
        </SectionCard>
      )}

      {/* Country Information */}
      {result.country && (
        <SectionCard title="Country Information" accent="#fbbf24">
          <div className="flex items-center gap-4 mb-5">
            {result.country.flagUrl
              ? <img src={result.country.flagUrl} alt={result.country.name}
                     className="w-16 h-10 object-cover rounded shadow-lg border border-white/10" />
              : <span className="text-5xl leading-none">{result.country.flag}</span>
            }
            <div>
              <div className="text-white text-xl font-semibold">{result.country.name}</div>
              <div className="text-[#00d4ff]/50 text-xs font-mono mt-1">
                {[result.country.region, result.country.subregion].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoRow label="Capital"    value={result.country.capital} />
            <InfoRow label="Population" value={result.country.population?.toLocaleString()} />
            <InfoRow label="Currency"   value={result.country.currencies} />
            <InfoRow label="Languages"  value={result.country.languages} />
            <InfoRow label="TLD"        value={result.country.tld} mono />
            {result.country.borders?.length > 0 && (
              <div>
                <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-1">Borders</div>
                <div className="flex flex-wrap gap-1">
                  {result.country.borders.map((b) => (
                    <span key={b} className="font-mono text-xs text-[#fbbf24] bg-[#060d18] border border-[#fbbf24]/20 px-2 py-0.5 rounded">
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Network */}
      {result.network && (
        <SectionCard title="Network & Routing" accent="#00ff88">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow label="ASN"             value={result.network.asn}           mono />
            <InfoRow label="Organization"    value={result.network.organization} />
            <InfoRow label="Description"     value={result.network.description} />
            <InfoRow label="ASN Country"     value={result.network.asnCountry} />
            <InfoRow label="Prefix"          value={result.network.prefix}        mono />
            <InfoRow label="Prefix Name"     value={result.network.prefixName} />
            <InfoRow label="RIR"             value={result.network.rir} />
            <InfoRow label="IP Range"        value={result.network.ipRange}       mono />
            <InfoRow label="Network Handle"  value={result.network.handle}        mono />
            <InfoRow label="Network Name"    value={result.network.networkName} />
            <InfoRow label="Hostname (rDNS)" value={result.network.hostname}      mono />
          </div>
        </SectionCard>
      )}

      {/* Security */}
      {result.security?.abuseEmail && (
        <SectionCard title="Security" accent="#ff4d6d">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow label="Abuse Contact (RDAP)" value={result.security.abuseEmail} mono />
          </div>
        </SectionCard>
      )}

    </div>
  );
}

// ─── DomainResult ─────────────────────────────────────────────────────────────
function DomainResult({ result }) {
  const coords = result.ipInfo?.geolocation?.coordinates;
  const mapLabel = result.domain;

  return (
    <div className="space-y-4">

      {/* Basic Info */}
      <SectionCard title="Basic Information" accent="#00d4ff">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-1">Domain</div>
            <div className="font-mono text-[#00d4ff] text-xl tracking-wider">{result.domain}</div>
          </div>
          {result.sources?.length > 0 && (
            <div>
              <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-2">Data Sources</div>
              <div className="flex flex-wrap gap-1">
                {result.sources.map((s) => (
                  <span key={s} className="text-xs font-mono bg-[#060d18] text-[#00d4ff]/60 border border-[#00d4ff]/15 px-2 py-0.5 rounded">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          {result.cached && (
            <span className="inline-flex items-center gap-1.5 text-xs font-mono bg-[#fbbf24]/10 text-[#fbbf24] border border-[#fbbf24]/20 px-2.5 py-1 rounded w-fit">
              ⚡ cached
            </span>
          )}
        </div>
      </SectionCard>

      {/* DNS Records */}
      {result.dns && (
        <SectionCard title="DNS Records" accent="#a78bfa">
          <div className="space-y-4">
            <div>
              <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-1">Status</div>
              <span className={`font-mono text-sm ${result.dns.status === 0 ? 'text-[#00ff88]' : 'text-[#ff4d6d]'}`}>
                {result.dns.status === 0 ? '● NOERROR' : `● ${result.dns.status}`}
              </span>
            </div>
            <DnsRecordList label="A Records (IPv4)"         records={result.dns.aRecords}    getVal={(r) => r.data} />
            <DnsRecordList label="AAAA Records (IPv6)"      records={result.dns.aaaaRecords} getVal={(r) => r.data} />
            <DnsRecordList label="MX Records (Mail)"        records={result.dns.mxRecords}   getVal={(r) => r.data} />
            <DnsRecordList label="NS Records (Nameservers)" records={result.dns.nsRecords}   getVal={(r) => r} />
            <DnsRecordList label="TXT Records"              records={result.dns.txtRecords}  getVal={(r) => r} />
            {result.dns.soa && (
              <div>
                <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-1">SOA Record</div>
                <div className="font-mono text-xs text-[#00ff88] bg-[#060d18] px-3 py-2 rounded border border-white/5 space-y-0.5">
                  <div><span className="text-[#00d4ff]/40">ns: </span>{result.dns.soa.nsname}</div>
                  <div><span className="text-[#00d4ff]/40">admin: </span>{result.dns.soa.hostmaster}</div>
                  <div><span className="text-[#00d4ff]/40">serial: </span>{result.dns.soa.serial} <span className="text-[#00d4ff]/40 ml-2">refresh: </span>{result.dns.soa.refresh}s <span className="text-[#00d4ff]/40 ml-2">ttl: </span>{result.dns.soa.minttl}s</div>
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Server Location + Map */}
      {result.ipInfo && (
        <SectionCard title="Server Location" accent="#00d4ff">
          {result.ipInfo.ip && (
            <div className="font-mono text-[#00d4ff]/50 text-sm mb-4">{result.ipInfo.ip}</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoRow label="Country"  value={result.ipInfo.geolocation?.country} />
            <InfoRow label="Region"   value={result.ipInfo.geolocation?.region} />
            <InfoRow label="City"     value={result.ipInfo.geolocation?.city} />
            <InfoRow label="Timezone" value={result.ipInfo.geolocation?.timezone} />
            <InfoRow label="ASN"      value={result.ipInfo.network?.asn}     mono />
            <InfoRow label="ISP"      value={result.ipInfo.network?.isp} />
            <InfoRow label="Prefix"   value={result.ipInfo.network?.prefix}  mono />
            <InfoRow label="RIR"      value={result.ipInfo.network?.rir} />
          </div>
          <MapView coordinates={coords} label={mapLabel} />
        </SectionCard>
      )}

      {/* SSL Certificate */}
      {result.ssl && (
        <SectionCard title="SSL Certificate" accent="#00ff88">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoRow label="Subject"  value={result.ssl.subject}  mono />
            <InfoRow label="Issuer"   value={result.ssl.issuer} />
            <InfoRow label="Issued By (CN)" value={result.ssl.issuerCN} />
            <InfoRow label="Valid From" value={result.ssl.validFrom} />
            <InfoRow label="Valid To"   value={result.ssl.validTo} />
            <div>
              <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-1">Days Until Expiry</div>
              <div className={`font-mono text-sm font-semibold ${
                result.ssl.expired            ? 'text-[#ff4d6d]' :
                result.ssl.daysUntilExpiry < 30  ? 'text-[#ff4d6d]' :
                result.ssl.daysUntilExpiry < 90  ? 'text-[#fbbf24]' :
                'text-[#00ff88]'
              }`}>
                {result.ssl.expired ? '⚠ EXPIRED' : `${result.ssl.daysUntilExpiry} days`}
              </div>
            </div>
            {result.ssl.sans?.length > 0 && (
              <div className="md:col-span-2 lg:col-span-3">
                <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-2">
                  Subject Alternative Names ({result.ssl.sans.length})
                </div>
                <div className="flex flex-wrap gap-1">
                  {result.ssl.sans.slice(0, 12).map((s, i) => (
                    <span key={i} className="font-mono text-xs text-[#00ff88]/80 bg-[#060d18] border border-[#00ff88]/15 px-2 py-0.5 rounded">
                      {s}
                    </span>
                  ))}
                  {result.ssl.sans.length > 12 && (
                    <span className="text-xs text-[#00d4ff]/30 font-mono px-2 py-0.5">
                      +{result.ssl.sans.length - 12} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Email Security */}
      {result.emailSecurity && (
        <SectionCard title="Email Security (SPF / DMARC)" accent="#a78bfa">
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-mono font-semibold px-2 py-0.5 rounded ${
                  result.emailSecurity.spf
                    ? 'bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/20'
                    : 'bg-[#ff4d6d]/10 text-[#ff4d6d] border border-[#ff4d6d]/20'
                }`}>
                  {result.emailSecurity.spf ? '✓ SPF' : '✗ SPF missing'}
                </span>
              </div>
              {result.emailSecurity.spf && (
                <div className="font-mono text-xs text-[#c8d8e8] bg-[#060d18] px-3 py-2 rounded border border-white/5 break-all">
                  {result.emailSecurity.spf}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-mono font-semibold px-2 py-0.5 rounded ${
                  result.emailSecurity.dmarc
                    ? 'bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/20'
                    : 'bg-[#ff4d6d]/10 text-[#ff4d6d] border border-[#ff4d6d]/20'
                }`}>
                  {result.emailSecurity.dmarc ? '✓ DMARC' : '✗ DMARC missing'}
                </span>
              </div>
              {result.emailSecurity.dmarc && (
                <div className="font-mono text-xs text-[#c8d8e8] bg-[#060d18] px-3 py-2 rounded border border-white/5 break-all">
                  {result.emailSecurity.dmarc}
                </div>
              )}
            </div>
          </div>
        </SectionCard>
      )}

      {/* Country Information */}
      {result.country && (
        <SectionCard title="Country Information" accent="#fbbf24">
          <div className="flex items-center gap-4 mb-5">
            {result.country.flagUrl
              ? <img src={result.country.flagUrl} alt={result.country.name}
                     className="w-16 h-10 object-cover rounded shadow-lg border border-white/10" />
              : <span className="text-5xl leading-none">{result.country.flag}</span>
            }
            <div>
              <div className="text-white text-xl font-semibold">{result.country.name}</div>
              <div className="text-[#00d4ff]/50 text-xs font-mono mt-1">
                {[result.country.region, result.country.subregion].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoRow label="Capital"    value={result.country.capital} />
            <InfoRow label="Population" value={result.country.population?.toLocaleString()} />
            <InfoRow label="Currency"   value={result.country.currencies} />
            <InfoRow label="Languages"  value={result.country.languages} />
            <InfoRow label="TLD"        value={result.country.tld} mono />
            {result.country.borders?.length > 0 && (
              <div>
                <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-1">Borders</div>
                <div className="flex flex-wrap gap-1">
                  {result.country.borders.map((b) => (
                    <span key={b} className="font-mono text-xs text-[#fbbf24] bg-[#060d18] border border-[#fbbf24]/20 px-2 py-0.5 rounded">
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* RDAP Registration */}
      {result.whois && (
        <SectionCard title="Registration (RDAP)" accent="#fbbf24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow label="Registrar"    value={result.whois.registrar} />
            <InfoRow label="Handle"       value={result.whois.handle}          mono />
            <InfoRow label="Created"      value={formatDate(result.whois.creationDate)} />
            <InfoRow label="Expires"      value={formatDate(result.whois.expirationDate)} />
            <InfoRow label="Last Updated" value={formatDate(result.whois.updatedDate)} />
            {result.whois.status?.length > 0 && (
              <div>
                <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-1">Status</div>
                <div className="space-y-0.5">
                  {(Array.isArray(result.whois.status) ? result.whois.status : [result.whois.status])
                    .map((s, i) => <div key={i} className="text-[#c8d8e8] text-sm">{s}</div>)}
                </div>
              </div>
            )}
            {result.whois.nameServers?.length > 0 && (
              <div className="md:col-span-2">
                <div className="text-[#00d4ff]/50 text-xs uppercase tracking-wider mb-2">Name Servers</div>
                <div className="flex flex-wrap gap-2">
                  {result.whois.nameServers.map((ns, i) => (
                    <span key={i} className="font-mono text-xs text-[#a78bfa] bg-[#060d18] border border-[#a78bfa]/20 px-2 py-0.5 rounded">
                      {ns}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

    </div>
  );
}

// ─── InfoSections (shown on homepage when no search results) ─────────────────
function InfoSections() {
  const features = [
    { icon: '🌍', title: 'IP Geolocation', desc: 'Country, city, region, coordinates and timezone for any IPv4 or IPv6 address.' },
    { icon: '🏢', title: 'ASN & ISP Info', desc: 'Autonomous System Number, organization, network prefix and RIR allocation.' },
    { icon: '📋', title: 'DNS Records', desc: 'A, AAAA, MX, NS, TXT, SOA records — complete DNS profile of any domain.' },
    { icon: '🔒', title: 'SSL Certificate', desc: 'Certificate issuer, validity dates, days until expiry and Subject Alt Names.' },
    { icon: '📧', title: 'Email Security', desc: 'SPF and DMARC records — check if a domain is protected against email spoofing.' },
    { icon: '📄', title: 'WHOIS / RDAP', desc: 'Domain registrar, registration date, expiry date, nameservers and status.' },
  ];

  const faqs = [
    {
      q: 'What is an IP address?',
      a: 'An IP address (Internet Protocol address) is a unique numerical label assigned to every device connected to a network. IPv4 addresses look like 192.168.1.1, while IPv6 addresses are longer (e.g. 2001:db8::1).',
    },
    {
      q: 'What is an ASN?',
      a: 'An Autonomous System Number (ASN) identifies a network operated by a single organization (e.g. AS15169 for Google). ASNs are used by internet routers to exchange routing information via BGP.',
    },
    {
      q: 'What is a DNS record?',
      a: 'DNS (Domain Name System) records map domain names to IP addresses and other data. A records point to IPv4 addresses, MX records handle email routing, TXT records store verification and security data (SPF, DMARC), and NS records define authoritative nameservers.',
    },
    {
      q: 'What is SPF and DMARC?',
      a: 'SPF (Sender Policy Framework) and DMARC (Domain-based Message Authentication) are email security standards stored as DNS TXT records. They protect domains from email spoofing and phishing by specifying which servers are authorised to send email on behalf of a domain.',
    },
    {
      q: 'How accurate is the geolocation data?',
      a: 'Geolocation is based on MaxMind GeoLite2 databases and is accurate to the country level in nearly all cases. City-level accuracy varies — it is typically correct for most ISPs but may be off for VPNs, proxies, or corporate networks that route traffic through central locations.',
    },
    {
      q: 'Is this tool free to use?',
      a: 'Yes. IP & Domain Tracker is completely free, requires no registration and no API key. Data is sourced from MaxMind GeoLite2 (local database), BGPView, RDAP/IANA, and standard DNS lookups.',
    },
  ];

  return (
    <div className="space-y-10 mt-6">

      {/* About */}
      <div className="rounded-2xl p-8 bg-[#0a1628]/60 border border-white/5 text-center">
        <h2 className="text-2xl font-bold text-white mb-3">
          Free IP Address &amp; Domain Intelligence Tool
        </h2>
        <p className="text-[#8aaac8] text-base max-w-2xl mx-auto leading-relaxed">
          Instantly look up detailed network information for any IP address or domain name.
          No registration required. Data is aggregated from multiple trusted sources including
          MaxMind GeoLite2, BGPView, RDAP/IANA and live DNS lookups.
        </p>
        <div className="flex flex-wrap justify-center gap-3 mt-5">
          {['Geolocation', 'ASN / ISP', 'DNS Records', 'SSL Certificate', 'SPF & DMARC', 'WHOIS / RDAP'].map(tag => (
            <span key={tag} className="text-xs font-mono text-[#00d4ff]/70 border border-[#00d4ff]/20 bg-[#00d4ff]/5 px-3 py-1 rounded-full">
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Features */}
      <div>
        <h2 className="text-sm font-mono font-semibold uppercase tracking-widest text-[#00d4ff] mb-5 text-center">
          {'// '} What You Can Look Up
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f) => (
            <div key={f.title}
              className="rounded-xl p-5 bg-[#0a1628]/80 border border-white/5 hover:border-[#00d4ff]/20 transition-colors">
              <div className="text-2xl mb-2">{f.icon}</div>
              <div className="text-white font-semibold mb-1">{f.title}</div>
              <div className="text-[#6a8aaa] text-sm leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* How to use */}
      <div className="rounded-2xl p-6 bg-[#0a1628]/60 border border-white/5">
        <h2 className="text-sm font-mono font-semibold uppercase tracking-widest text-[#00d4ff] mb-5">
          {'// '} How to Use
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { step: '01', title: 'Choose type', desc: 'Select "IP Address" or "Domain" depending on what you want to look up.' },
            { step: '02', title: 'Enter query', desc: 'Type an IPv4/IPv6 address (e.g. 8.8.8.8) or a domain name (e.g. google.com).' },
            { step: '03', title: 'Get results', desc: 'Instantly receive geolocation, network, DNS, SSL, and registration data.' },
          ].map((s) => (
            <div key={s.step} className="flex gap-4">
              <div className="text-3xl font-bold font-mono text-[#00d4ff]/15 shrink-0">{s.step}</div>
              <div>
                <div className="text-white font-semibold mb-1">{s.title}</div>
                <div className="text-[#6a8aaa] text-sm">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Data sources */}
      <div className="rounded-2xl p-6 bg-[#0a1628]/60 border border-white/5">
        <h2 className="text-sm font-mono font-semibold uppercase tracking-widest text-[#00d4ff] mb-5">
          {'// '} Data Sources
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { name: 'MaxMind GeoLite2', type: 'Local database', desc: 'Industry-standard geolocation and ASN database, updated monthly.' },
            { name: 'BGPView', type: 'Free API', desc: 'Network prefix, ASN and routing information.' },
            { name: 'RDAP / IANA', type: 'Free API', desc: 'Official domain and IP registration data via the IANA RDAP protocol.' },
            { name: 'Node.js DNS resolver', type: 'Built-in', desc: 'Live DNS queries (A, AAAA, MX, NS, TXT, SOA) via system resolver.' },
            { name: 'TLS/SSL handshake', type: 'Built-in', desc: 'Direct TLS connection to retrieve certificate details in real time.' },
            { name: 'RestCountries', type: 'Free API', desc: 'Country metadata — capital, population, currency, languages, flag.' },
          ].map((src) => (
            <div key={src.name} className="flex gap-3 items-start">
              <span className="text-xs font-mono text-[#00ff88] bg-[#00ff88]/8 border border-[#00ff88]/20 px-2 py-0.5 rounded shrink-0 mt-0.5">
                {src.type}
              </span>
              <div>
                <div className="text-[#c8d8e8] text-sm font-medium">{src.name}</div>
                <div className="text-[#6a8aaa] text-xs mt-0.5">{src.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div>
        <h2 className="text-sm font-mono font-semibold uppercase tracking-widest text-[#00d4ff] mb-5 text-center">
          {'// '} Frequently Asked Questions
        </h2>
        <div className="space-y-3">
          {faqs.map((faq) => (
            <div key={faq.q} className="rounded-xl p-5 bg-[#0a1628]/80 border border-white/5">
              <div className="text-white font-semibold mb-2">{faq.q}</div>
              <div className="text-[#6a8aaa] text-sm leading-relaxed">{faq.a}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Learn articles */}
      <div>
        <h2 className="text-sm font-mono font-semibold uppercase tracking-widest text-[#00d4ff] mb-5 text-center">
          {'// '} Learn More — Networking Guides
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { href: '/ip-geolocation-explained.html', title: 'IP Geolocation Explained', desc: 'How IP geolocation works, its accuracy and why locations can be wrong.', time: '6 min', color: '#00d4ff' },
            { href: '/what-is-asn.html', title: 'What is an ASN?', desc: 'Understand Autonomous Systems, BGP routing and network ownership.', time: '6 min', color: '#f97316' },
            { href: '/email-security-spf-dmarc.html', title: 'SPF & DMARC Email Security', desc: 'Learn how SPF and DMARC help protect domains against spoofing.', time: '7 min', color: '#fbbf24' },
            { href: '/what-is-an-ip-address.html',        title: 'What is an IP Address?',        desc: 'IPv4 vs IPv6, public vs private, static vs dynamic — the complete primer.', time: '5 min', color: '#00ff88' },
            { href: '/how-does-dns-work.html',             title: 'How Does DNS Work?',             desc: 'DNS resolution, record types (A, MX, TXT, SOA), TTL and DNS privacy.', time: '6 min', color: '#a78bfa' },
            { href: '/guides.html',                        title: 'All Networking Guides →',        desc: 'Browse all networking guides on IP, DNS, security, and troubleshooting.', time: 'all guides', color: '#00d4ff' },
          ].map((art) => (
            <a
              key={art.href}
              href={art.href}
              className="rounded-xl p-5 bg-[#0a1628]/80 border border-white/5 hover:border-[#00d4ff]/20 transition-colors block no-underline group"
              style={{ textDecoration: 'none' }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono px-2 py-0.5 rounded-full border"
                  style={{ color: art.color, borderColor: `${art.color}30`, background: `${art.color}10` }}>
                  {art.time} read
                </span>
                <span className="text-[#00d4ff]/30 text-sm group-hover:text-[#00d4ff]/60 transition-colors">→</span>
              </div>
              <div className="text-white font-semibold mb-1 text-sm">{art.title}</div>
              <div className="text-[#6a8aaa] text-xs leading-relaxed">{art.desc}</div>
            </a>
          ))}
        </div>
      </div>

    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  const [query, setQuery]         = useState('');
  const [result, setResult]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [searchType, setSearchType] = useState('ip');
  const [history, setHistory]     = useState(loadHistory);

  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

  // ── My IP ─────────────────────────────────────────────────────────────────
  const handleMyIp = async () => {
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/ip/me`);
      setResult(response.data);
      setQuery(response.data.ip);
      setSearchType('ip');
      saveToHistory('ip', response.data.ip, response.data);
      toast.success('Your IP detected');
    } catch (error) {
      toast.error('Could not detect your IP');
    } finally {
      setLoading(false);
    }
  };

  // ── History ────────────────────────────────────────────────────────────────
  const saveToHistory = (type, q, data) => {
    const preview = type === 'ip'
      ? [data.geolocation?.city, data.geolocation?.country].filter(Boolean).join(', ')
      : data.ipInfo?.geolocation?.country || '';

    const entry = { id: Date.now(), type, query: q, timestamp: new Date().toISOString(), preview };

    setHistory((prev) => {
      const updated = [entry, ...prev.filter((h) => !(h.query === q && h.type === type))].slice(0, MAX_HISTORY);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem(HISTORY_KEY);
    toast.success('History cleared');
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const runSearch = async (q, type) => {
    if (!q?.trim()) { toast.error('Enter an IP address or domain'); return; }
    setLoading(true);
    setResult(null);
    try {
      const endpoint = type === 'ip' ? 'ip' : 'domain';
      const response = await axios.get(`${API_BASE_URL}/${endpoint}/${encodeURIComponent(q.trim())}`);
      setResult(response.data);
      saveToHistory(type, q.trim(), response.data);
      toast.success(response.data.cached ? 'Loaded from cache' : 'Scan complete');
    } catch (error) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.error || 'Failed to perform search');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); runSearch(query, searchType); };

  const handleHistoryClick = (entry) => {
    setQuery(entry.query);
    setSearchType(entry.type);
    runSearch(entry.query, entry.type);
  };

  const isValidIP = (ip) => {
    const v4 = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/;
    const v6 = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    return v4.test(ip) || v6.test(ip);
  };

  const isValidDomain = (d) =>
    /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(d);

  const handleInputChange = (value) => {
    setQuery(value);
    if (isValidIP(value)) setSearchType('ip');
    else if (isValidDomain(value)) setSearchType('domain');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #060d18 0%, #0a1628 100%)' }}>

      {/* Scanner line */}
      {loading && <div className="scanner-line" />}

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#0a1628',
            color: '#c8d8e8',
            border: '1px solid rgba(0,212,255,0.15)',
            fontFamily: 'monospace',
            fontSize: '13px',
          },
        }}
      />

      {/* Navigation bar */}
      <nav style={{ background: 'rgba(6,13,24,0.95)', borderBottom: '1px solid rgba(0,212,255,0.08)', backdropFilter: 'blur(8px)' }}
           className="sticky top-0 z-50 w-full">
        <div className="container mx-auto px-4 max-w-5xl flex items-center justify-between h-12">
          <a href="/" className="text-[#00d4ff] font-mono text-sm font-semibold no-underline" style={{ textDecoration: 'none' }}>
            ip-tracker.eu
          </a>
          <div className="flex items-center gap-5">
            <a href="/guides.html" className="text-[#8aaac8] hover:text-[#00d4ff] font-mono text-xs transition-colors no-underline" style={{ textDecoration: 'none' }}>Guides</a>
            <a href="/what-is-an-ip-address.html" className="text-[#8aaac8] hover:text-[#00d4ff] font-mono text-xs transition-colors no-underline hidden sm:block" style={{ textDecoration: 'none' }}>IP Address</a>
            <a href="/how-does-dns-work.html" className="text-[#8aaac8] hover:text-[#00d4ff] font-mono text-xs transition-colors no-underline hidden sm:block" style={{ textDecoration: 'none' }}>DNS</a>
            <a href="/what-is-a-vpn.html" className="text-[#8aaac8] hover:text-[#00d4ff] font-mono text-xs transition-colors no-underline hidden md:block" style={{ textDecoration: 'none' }}>VPN</a>
            <a href="/about.html" className="text-[#8aaac8] hover:text-[#00d4ff] font-mono text-xs transition-colors no-underline" style={{ textDecoration: 'none' }}>About</a>
            <a href="/contact.html" className="text-[#8aaac8] hover:text-[#00d4ff] font-mono text-xs transition-colors no-underline" style={{ textDecoration: 'none' }}>Contact</a>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-10 max-w-5xl">

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-mono text-[#00d4ff]/40
                          uppercase tracking-widest mb-5 border border-[#00d4ff]/10
                          px-4 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 bg-[#00ff88] rounded-full animate-pulse inline-block" />
            System online
          </div>
          <h1 className="text-5xl font-bold tracking-tight mb-3">
            <span className="text-white">IP </span>
            <span style={{ color: '#00d4ff' }}>&</span>
            <span className="text-white"> Domain Tracker</span>
          </h1>
          <p className="text-[#2a4060] font-mono text-sm tracking-wider">
            network intelligence · geolocation · asn · rdap
          </p>
        </div>

        {/* Search Card */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="rounded-2xl p-6 bg-[#0a1628]/90 border border-[#00d4ff]/10"
               style={{ boxShadow: '0 0 60px rgba(0,212,255,0.03)' }}>
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Type tabs */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSearchType('ip')}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-mono
                              transition-all border ${
                    searchType === 'ip'
                      ? 'bg-[#00d4ff]/10 text-[#00d4ff] border-[#00d4ff]/30'
                      : 'text-[#2a4060] border-white/5 hover:text-[#00d4ff]/50 hover:border-[#00d4ff]/15'
                  }`}
                >
                  <ComputerDesktopIcon className="w-4 h-4" />
                  IP Address
                </button>
                <button
                  type="button"
                  onClick={() => setSearchType('domain')}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-mono
                              transition-all border ${
                    searchType === 'domain'
                      ? 'bg-[#00ff88]/10 text-[#00ff88] border-[#00ff88]/30'
                      : 'text-[#2a4060] border-white/5 hover:text-[#00ff88]/50 hover:border-[#00ff88]/15'
                  }`}
                >
                  <GlobeAltIcon className="w-4 h-4" />
                  Domain
                </button>
              </div>

              {/* Input */}
              <div className="relative">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => handleInputChange(e.target.value)}
                  placeholder={searchType === 'ip' ? '8.8.8.8' : 'google.com'}
                  className="w-full px-5 py-4 font-mono text-[#e2e8f0] text-base bg-[#060d18] rounded-xl
                             border border-[#00d4ff]/10 focus:border-[#00d4ff]/40 focus:outline-none
                             focus:ring-2 focus:ring-[#00d4ff]/8 transition-all placeholder-[#1a2f45]"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="absolute right-2 top-2 flex items-center gap-2 px-4 py-2.5 rounded-lg
                             text-sm font-mono font-medium transition-all"
                  style={{
                    background: 'rgba(0,212,255,0.08)',
                    color: loading ? 'rgba(0,212,255,0.4)' : '#00d4ff',
                    border: '1px solid rgba(0,212,255,0.2)',
                  }}
                >
                  {loading
                    ? <div className="w-4 h-4 border-2 border-[#00d4ff]/40 border-t-[#00d4ff] rounded-full animate-spin" />
                    : <MagnifyingGlassIcon className="w-4 h-4" />
                  }
                  {loading ? 'scanning...' : 'search'}
                </button>
              </div>
              {/* My IP shortcut */}
              {searchType === 'ip' && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleMyIp}
                    disabled={loading}
                    className="text-xs font-mono text-[#00d4ff]/35 hover:text-[#00d4ff]/70
                               transition-colors flex items-center gap-1.5"
                  >
                    <span className="w-1.5 h-1.5 bg-[#00ff88] rounded-full animate-pulse inline-block" />
                    What is my IP?
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* History */}
        <HistoryPanel history={history} onSelect={handleHistoryClick} onClear={clearHistory} />

        {/* Ad — above results */}
        {result && (
          <div className="max-w-2xl mx-auto mb-6">
            <AdBanner slot="3090734165" format="horizontal" />
          </div>
        )}

        {/* Results */}
        {result && (
          <div>
            <div className="rounded-2xl p-6 bg-[#0a1628]/60 border border-white/5 mb-8">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/5">
                <h2 className="text-sm font-mono font-semibold text-[#00d4ff] uppercase tracking-widest">
                  {searchType === 'ip' ? '> ip_lookup' : '> domain_lookup'}
                </h2>
                <div className="text-xs font-mono text-[#1a2f45]">
                  {new Date(result.timestamp).toLocaleString()}
                </div>
              </div>

              {searchType === 'ip'
                ? <IPResult result={result} />
                : <DomainResult result={result} />
              }
            </div>
          </div>
        )}

        {/* Ad — below results */}
        {result && (
          <div className="mb-6">
            <AdBanner slot="3681761109" format="auto" />
          </div>
        )}

        {/* Info sections — shown when no results (helps with SEO and AdSense content policy) */}
        {!result && (
          <>
            <div className="max-w-2xl mx-auto mb-8">
              <AdBanner slot="3090734165" format="auto" />
            </div>
            <InfoSections />
          </>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-xs font-mono text-[#1a2f45] space-y-3">
          <div>maxmind · bgpview · rdap · dns</div>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            <a href="/guides.html" className="text-[#1a2f45] hover:text-[#00d4ff]/50 transition-colors">Guides</a>
            <a href="/about.html" className="text-[#1a2f45] hover:text-[#00d4ff]/50 transition-colors">About</a>
            <a href="/contact.html" className="text-[#1a2f45] hover:text-[#00d4ff]/50 transition-colors">Contact</a>
            <a href="/terms.html" className="text-[#1a2f45] hover:text-[#00d4ff]/50 transition-colors">Terms</a>
            <a href="/privacy-policy.html" className="text-[#1a2f45] hover:text-[#00d4ff]/50 transition-colors">Privacy</a>
          </div>
        </div>

      </div>
    </div>
  );
}

export default App;
